import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateBannerMessage, fetchBannerMessage, fetchAppSettings, updateAppSettings } from '../../store/slices/settingsSlice';
import LoadingSpinner from '../common/LoadingSpinner';

const AppSettingsTab = () => {
  const dispatch = useDispatch();
  const { loading, bannerMessage } = useSelector(state => state.settings);
  const { user } = useSelector(state => state.auth);

  const [bannerText, setBannerText] = useState('');
  const [bannerEnabled, setBannerEnabled] = useState(false);
  const [bannerColor, setBannerColor] = useState('blue');
  const [autoHideAfter, setAutoHideAfter] = useState(0);
  const [visibleTo, setVisibleTo] = useState(['super_admin', 'admin', 'project_manager']);
  const [jwtExpirationHours, setJwtExpirationHours] = useState(6);

  // Crew roles are no longer supported via settings
  const BANNER_ROLES = ['super_admin', 'admin', 'project_manager'];
  const HIDDEN_ROLES = ['crew_leader', 'crew_member'];

  // Fetch settings on component mount
  React.useEffect(() => {
    dispatch(fetchBannerMessage());
    dispatch(fetchAppSettings());
  }, [dispatch]);

  // Update local state when banner message changes
  React.useEffect(() => {
    if (bannerMessage) {
      setBannerText(bannerMessage.text || '');
      setBannerEnabled(bannerMessage.isEnabled || false);
      setBannerColor(bannerMessage.backgroundColor || 'blue');
      setAutoHideAfter(bannerMessage.autoHideAfter || 0);
      const incomingVisibleTo = bannerMessage.visibleTo || BANNER_ROLES;
      setVisibleTo(incomingVisibleTo.filter((role) => !HIDDEN_ROLES.includes(role)));
    }
  }, [bannerMessage]);

  const inputClasses = `
    block w-full text-sm text-gray-500
    file:mr-4 file:py-2.5 file:px-4
    file:rounded-md file:border-0
    file:text-sm file:font-semibold
    file:bg-blue-50 file:text-blue-700
    hover:file:bg-blue-100 transition-colors
    border border-gray-200 rounded-md
  `;

  const buttonClasses = `
    px-4 py-2.5 rounded-md text-white
    bg-blue-600 hover:bg-blue-700
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50
    transition-colors disabled:opacity-50 disabled:cursor-not-allowed
  `;

  if (loading) {
    return <LoadingSpinner />;
  }



  return (
    <div>
      <h3 className="text-lg font-medium mb-4">App Settings</h3>

      <div className="space-y-6">
        {/* JWT Settings - Only visible to super admin */}
        {user?.profile?.role === 'super_admin' && (
          <div className="border-b pb-6">
            <h4 className="text-md font-medium mb-4">Session Settings</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Session Duration (Hours)
                </label>
                <select
                  value={jwtExpirationHours}
                  onChange={(e) => setJwtExpirationHours(Number(e.target.value))}
                  className="block w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                >
                  <option value={1}>1 hour</option>
                  <option value={2}>2 hours</option>
                  <option value={4}>4 hours</option>
                  <option value={6}>6 hours</option>
                  <option value={8}>8 hours</option>
                  <option value={12}>12 hours</option>
                  <option value={24}>24 hours</option>
                  <option value={48}>48 hours</option>
                  <option value={72}>72 hours</option>
                </select>
              </div>
              <div>
                <button
                  onClick={() => {
                    dispatch(updateAppSettings({
                      jwtExpirationHours
                    }));
                  }}
                  className={buttonClasses}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Session Settings'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Banner Message Section - Only visible to super admin */}
        {user?.profile?.role === 'super_admin' && (
          <div className="border-b pb-6">
            <h4 className="text-md font-medium mb-4">Banner Message</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message Text
                </label>
                <input
                  type="text"
                  value={bannerText}
                  onChange={(e) => setBannerText(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter banner message"
                />
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={bannerEnabled}
                    onChange={(e) => setBannerEnabled(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-700">
                    Enable Banner
                  </label>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="flex items-center">
                    <label className="block text-sm text-gray-700 mr-2">
                      Color:
                    </label>
                    <select
                      value={bannerColor}
                      onChange={(e) => setBannerColor(e.target.value)}
                      className="block w-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      <option value="blue">Blue</option>
                      <option value="green">Green</option>
                      <option value="yellow">Yellow</option>
                      <option value="red">Red</option>
                    </select>
                  </div>

                  <div className="flex items-center">
                    <label className="block text-sm text-gray-700 mr-2">
                      Auto-hide after:
                    </label>
                    <select
                      value={autoHideAfter}
                      onChange={(e) => setAutoHideAfter(Number(e.target.value))}
                      className="block w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      <option value={0}>Never</option>
                      <option value={1}>1 minute</option>
                      <option value={2}>2 minutes</option>
                      <option value={5}>5 minutes</option>
                      <option value={10}>10 minutes</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Visible To
                </label>
                <div className="space-y-2">
                  {BANNER_ROLES.map((role) => (
                    <label key={role} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={visibleTo.includes(role)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setVisibleTo([...visibleTo, role]);
                          } else {
                            setVisibleTo(visibleTo.filter(r => r !== role));
                          }
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {role.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <button
                  onClick={() => {
                    dispatch(updateBannerMessage({
                      text: bannerText,
                      isEnabled: bannerEnabled,
                      backgroundColor: bannerColor,
                      autoHideAfter: autoHideAfter,
                      visibleTo: visibleTo
                    }));
                  }}
                  className={buttonClasses}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Banner Settings'}
                </button>
              </div>
            </div>
          </div>
        )}


      </div>
    </div>
  );
};

export default AppSettingsTab; 